import { memo, useCallback, useEffect, useState } from "react";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useSendEmail } from "../../api/mutations/useSendEmail";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../../Context/AppContext";
import type { Users } from "../../types/Types";

type ContactFormData = {
  name: string;
  email: string;
  subject: string;
  message: string;
};

const DEFAULT_FORM_VALUES: ContactFormData = {
  name: "",
  email: "",
  subject: "",
  message: "",
};

const schema = yup.object().shape({
  name: yup.string().required("Name is required"),
  email: yup
    .string()
    .email("Invalid email format")
    .required("Email is required"),
  subject: yup.string().required("Subject is required"),
  message: yup
    .string()
    .min(10, "Message must be at least 10 characters")
    .required("Message is required"),
});

const SUBMIT_ERROR_MESSAGE =
  "Failed to send message. Please try again later.";

function getDisplayName(user: Users | undefined): string {
  const first = user?.user?.firstName;
  const last = user?.user?.lastName;
  return [first, last].filter(Boolean).join(" ").trim() || "";
}

function getInputClassName(hasError: boolean, base = "form-control rounded-3") {
  return `${base} ${hasError ? "is-invalid" : ""}`.trim();
}

const SUBJECT_OPTIONS = [
  "Muslim Invest",
  "Company Enquiry",
  "Investor Enquiry",
  "Muslim Mentor Academy",
  "Mentor Enquiry",
  "Mentee Enquiry",
  "Muslim Venture Builder",
  "Muslim International Markets",
  "International Trade Program Enquiry",
  "Consultancy Services",
  "Market Access Support",
  "Business Accelerator",
  "Recruitment Support",
  "B2b Introduction",
  "Events Enquiry",
  "Isti’ana Board",
  "Raise a Complaint",
  "Upgrade Membership",
  "Cancel Membership",
  "Other",
];

function ContactForm() {
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ContactFormData>({
    resolver: yupResolver(schema),
    defaultValues: DEFAULT_FORM_VALUES,
  });

  const navigate = useNavigate();
  const [submitError, setSubmitError] = useState("");
  const { user } = useAppContext();

  const onSuccess = useCallback(() => {
    navigate("/contact-confirmation");
  }, [navigate]);

  const onError = useCallback(() => {
    setSubmitError(SUBMIT_ERROR_MESSAGE);
  }, []);

  const { mutate: sendMail, isPending } = useSendEmail({
    onSuccess,
    onError,
  });

  const onSubmit: SubmitHandler<ContactFormData> = useCallback(
    (data) => {
      setSubmitError("");
      
      // Get current user information
      const userName = getDisplayName(user);
      const userEmail = user?.user?.email ?? "";
      
      // Append user signature to email body
      let emailBody = data.message;
      if (userName || userEmail) {
        emailBody += "\n\n---\n\nBest Regards";
        if (userName) {
          emailBody += `\n${userName}`;
        }
        if (userEmail) {
          emailBody += `\n${userEmail}`;
        }
      }
      
      sendMail({
        email: "",
        subject: data.subject,
        body: emailBody,
        name: data.name,
      });
    },
    [sendMail, user]
  );

  useEffect(() => {
    if (user) {
      reset({
        ...DEFAULT_FORM_VALUES,
        name: getDisplayName(user),
        email: user.user?.email ?? "",
      });
    }
  }, [user, reset]);

  return (
    <div className="card shadow-lg border-0 rounded-2">
      <div className="card-header gradient-bg text-white d-flex justify-content-between align-items-center">
        <h4 className="mb-0">Contact Us</h4>
      </div>
      <div className="p-4">
        {submitError && (
          <div className="alert alert-danger rounded-3">{submitError}</div>
        )}

        <form
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          aria-label="Contact form"
        >
          <div className="d-flex flex-column flex-md-row gap-3 mb-4">
            <div className="flex-grow-1">
              <label htmlFor="name" className="form-label fw-semibold">
                Name
              </label>
              <Controller
                name="name"
                control={control}
                  render={({ field }) => (
                  <input
                    readOnly
                    type="text"
                    id="name"
                    className={getInputClassName(!!errors.name)}
                    placeholder="Your name"
                    {...field}
                  />
                )}
              />
              {errors.name && (
                <div className="invalid-feedback">{errors.name.message}</div>
              )}
            </div>

            <div className="flex-grow-1">
              <label htmlFor="email" className="form-label fw-semibold">
                Email
              </label>
              <Controller
                name="email"
                control={control}
                  render={({ field }) => (
                  <input
                    readOnly
                    type="email"
                    id="email"
                    className={getInputClassName(!!errors.email)}
                    placeholder="you@example.com"
                    {...field}
                  />
                )}
              />
              {errors.email && (
                <div className="invalid-feedback">{errors.email.message}</div>
              )}
            </div>
          </div>

          <div className="mb-4">
            <label htmlFor="subject" className="form-label fw-semibold">
              Subject
            </label>
            <Controller
              name="subject"
              control={control}
              render={({ field }) => (
                <select
                  id="subject"
                  className={getInputClassName(!!errors.subject, "form-select rounded-3")}
                  {...field}
                >
                  <option value="">Select</option>
                  {SUBJECT_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              )}
            />
            {errors.subject && (
              <div className="invalid-feedback">{errors.subject.message}</div>
            )}
          </div>

          <div className="mb-4">
            <label htmlFor="message" className="form-label fw-semibold">
              Message
            </label>
            <Controller
              name="message"
              control={control}
              render={({ field }) => (
                <textarea
                  id="message"
                  className={getInputClassName(!!errors.message)}
                  rows={5}
                  placeholder="Write your message here..."
                  {...field}
                />
              )}
            />
            {errors.message && (
              <div className="invalid-feedback">{errors.message.message}</div>
            )}
          </div>
          <button
            type="submit"
            className="btn btn-primary w-100"
            disabled={isPending}
          >
            {isPending ? "Sending..." : "Send Message"}
          </button>
        </form>
      </div>
    </div>
  );
}

ContactForm.displayName = "ContactForm";

export default memo(ContactForm);
